/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { mapObjIndexed, prop } from 'ramda'
import React from 'react'

import { Col, Grid } from '../structure/Grid'
import { applyStyle, getSmartSize } from '../../utils/helpers/styles'
import { useColor } from '../theme/_hooks/colors'
import { useGridStyle } from '../../utils/styles/grid'
import { usePaddingStyle } from '../../utils/styles/padding'
import { useSizeStyle } from '../../utils/styles/size'
import Line from '../helpers/Line'
import Panel from '../structure/Panel'
import RenderOnScreen from '../helpers/RenderOnScreen'
import Separator from '../helpers/Separator'
import Text from '../presentation/Text'

export const Table = ({ children }) => {
  const style = css`
    display: flex;
    flex-flow: column nowrap;
    line-height: 1.5;
    flex: 1 1 auto;
  `

  return (
    <div className="table" css={style}>
      {children}
    </div>
  )
}

const ROW_SIZES = {
  small: { head: 1.8, normal: 3, padding: 0.8 },
  normal: { head: 2, normal: 3.5, padding: 1 },
  medium: { head: 2.2, normal: 4, padding: 1.4 }
}

export const TableRow = ({
  children,
  disableRenderOnScreen,
  head,
  size,
  hasSidePadding,
  lineColor,
  showDeleted,
  ...props
}) => {
  const lineColorValue = useColor(lineColor || 'light3')
  const minHeightValue = getSmartSize(size, mapObjIndexed(prop(head ? 'head' : 'normal'), ROW_SIZES), 'normal')
  const sidePadding = getSmartSize(size, mapObjIndexed(prop('padding'), ROW_SIZES), 'normal')
  const style = css`
    width: 100%;
    display: flex;
    align-items: center;
    flex-flow: row nowrap;
    gap: 0.4rem;
    ${applyStyle('border-bottom', !head, `solid 1px ${lineColorValue}`)}
    ${applyStyle('min-height', minHeightValue)}
    ${applyStyle('padding', !head, '0.2rem 0')}
    ${applyStyle('padding-left', hasSidePadding, sidePadding)}
    ${applyStyle('padding-right', hasSidePadding, sidePadding)}
		${usePaddingStyle(props)}
  `

  return (
    <RenderOnScreen disabled={disableRenderOnScreen}>
      <div className={`tableRow ${head ? 'head' : ''}`} css={style}>
        {children}
      </div>
    </RenderOnScreen>
  )
}

export const TableCol = ({ children, raw, ...props }) => {
  const flex = props.width ? undefined : 1

  const style = css`
    ${useGridStyle({ flex, ...props })}
    ${useSizeStyle(props)}
  `

  const content = raw ? (
    children
  ) : (
    <Text size="small" {...props}>
      {children}
    </Text>
  )

  return (
    <div className="tableCol" css={style}>
      {content}
    </div>
  )
}

export const TableContent = ({ inPanel, children }) => {
  if (!inPanel) return children
  return (
    <Panel noPadding bgColor="transparent">
      {children}
    </Panel>
  )
}

export const ExpandedTableRow = ({ children, smallPadding }) => {
  return (
    <TableRow disableRenderOnScreen>
      <Grid align="stretch" width="100%">
        <Separator vertical weight={smallPadding ? 3 : 8} />
        <Col>
          <Line height="100%" vertical color="light3" />
        </Col>
        <Separator vertical weight={5} />
        <Col flex={1}>{children}</Col>
      </Grid>
    </TableRow>
  )
}
